<script setup lang="ts">
import { ref, computed } from "vue";
import { HomeService } from "~/services/HomeService";
import BaseLink from "@/components/buttons/BaseLink.vue";
import { VITE_BANNER_FASH_OFFER } from "~/config/constants.js";

interface PromotionBanner {
  id: number;
  image?: string;
  name: string;
  action_url?: string;
}

// state
const routeHydrated = useRouteHydrated();
const promotions = useState<PromotionBanner[]>("home.flashBanners", () => []);
const imageErrors = ref<Record<number, boolean>>({});
const isHovered = ref<boolean[]>([]);

// computed
const promotionsFinal = computed(() => promotions.value.slice(0, 6));

// lifecycle
const init = async () => {
  if (!routeHydrated.value) {
    const homeService = new HomeService();
    try {
      promotions.value = await homeService.getBanners(VITE_BANNER_FASH_OFFER);
    } catch (err) {
      console.error(err);
    }
  }
};

if (import.meta.server) {
  await init();
} else {
  init(); // en cliente mostramos pantallas de carga
}
</script>

<template>
  <section v-if="promotionsFinal.length > 0">
    <div
      class="row row-cols-1 row-cols-xl-3 gx-0 gy-2 mt-4 mt-lg-5 mw-100 mx-auto"
    >
      <div
        v-for="(promotion, index) in promotionsFinal"
        :key="index"
        class="col"
      >
        <base-link
          :to="promotion.action_url || '#'"
          class="promotion link-underline link-underline-opacity-0 position-relative px-1 px-lg-2 d-block"
          @mouseenter="isHovered[promotion.id] = true"
          @mouseleave="isHovered[promotion.id] = false"
        >
          <div
            class="border py-1 border-light-subtle d-flex justify-content-center align-items-center bg-light"
          >
            <div class="w-100 text-center">
              <img
                v-if="promotion.image && !imageErrors[promotion.id]"
                :src="promotion.image"
                :alt="promotion.name"
                width="600"
                height="250"
                class="image-promotion"
                :class="{ 'hover-promotion': isHovered[promotion.id] }"
                loading="lazy"
                decoding="async"
                @error="imageErrors[promotion.id] = true"
              />
            </div>
            <div
              v-if="imageErrors[promotion.id]"
              class="height-promotion d-flex justify-content-center align-items-center"
              :class="{ 'hover-promotion': isHovered[promotion.id] }"
            >
              <span
                class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
              >
                {{ promotion.name }}
              </span>
            </div>
            <span
              v-if="isHovered[promotion.id]"
              class="position-absolute top-50 start-50 translate-middle p-2 bg-dark text-white fs-7"
            >
              {{ $t("More information") }}
            </span>
          </div>
        </base-link>
      </div>
    </div>
  </section>
</template>

<style lang="sass" scoped>
.height-promotion
    height: 160px
    @media (min-width: 992px)
        height: 250px

.promotion
    @extend .height-promotion

.image-promotion
    @extend .height-promotion
    object-fit: contain
    object-position: center
    max-width: 100%

.hover-promotion
    filter: brightness(0.2)
</style>
